import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { LanguageService, LanguageCode } from '../../../services/i18n/language';
import { AuthService, UserDto } from '../../../services/auth/auth';
import { CurrencyRatesService } from '../../../services/currency-rates';
import { NotificationBellComponent } from '../notification-bell/notification-bell';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, DatePipe, NotificationBellComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  logoPath = 'assets/logo.png';
  currentDate = new Date();

  readonly currencyCodes = ['USD', 'EUR', 'CNY'];

  rates: Record<string, number> = { USD: 501, EUR: 597, CNY: 72 };

  currentLanguage: LanguageCode = 'ru';
  langMenuOpen = false;
  readonly languageOptions: { code: LanguageCode; label: string }[] = [
    { code: 'kk', label: 'KZ' },
    { code: 'ru', label: 'RU' },
    { code: 'en', label: 'EN' },
    { code: 'zh', label: 'ZH' }
  ];

  get currentLanguageLabel(): string {
    return this.languageOptions.find(l => l.code === this.currentLanguage)?.label || 'RU';
  }
  loginText = 'Войти';
  readonly whatsappUrl = 'https://wa.me/77078880054';

  /** WhatsApp с готовым текстом: на странице расчёта — с его номером, на остальных — общий вопрос. */
  get whatsappHref(): string {
    const calc = this.router.url.match(/^\/calculations\/(\d+)/);
    const text = calc
      ? (this.t('header.whatsappCalcMessage') || 'Здравствуйте! Вопрос по расчёту № {id} на Bilload').replace('{id}', calc[1])
      : (this.t('header.whatsappMessage') || 'Здравствуйте! У меня вопрос по Bilload');
    return `${this.whatsappUrl}?text=${encodeURIComponent(text)}`;
  }

  whatsappLabel = 'Написать в WhatsApp';
  myRequestsText = 'Личный кабинет';
  requestsText = 'Запросы';
  personalCabinetText = 'Личный кабинет';
  searchOrdersText = 'Поиск запросов';
  usersText = 'Админ-панель';
  isAuthenticated = false;
  isAdmin = false;
  isCargoOwner = false;
  isFreightForwarder = false;
  isCustomsBroker = false;
  isPublic = false;
  currentUser: UserDto | null = null;
  private subscriptions = new Subscription();

  constructor(
    public languageService: LanguageService,
    public authService: AuthService,
    private currencyRates: CurrencyRatesService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.langMenuOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.langMenuOpen = false;
    }
  }

  t(key: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? '' : value;
  }

  /** Навигация по разделам лендинга; с других страниц — сначала переход на главную. */
  goToSection(event: Event, id: string): void {
    event.preventDefault();
    const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    if (this.router.url.split(/[?#]/)[0] === '/') {
      scroll();
    } else {
      this.router.navigateByUrl('/').then(() => setTimeout(scroll));
    }
  }

  toggleLangMenu(): void {
    this.langMenuOpen = !this.langMenuOpen;
  }

  selectLanguage(lang: LanguageCode): void {
    this.switchLanguage(lang);
    this.langMenuOpen = false;
  }

  ngOnInit(): void {
    // Устанавливаем начальный язык
    this.currentLanguage = this.languageService.getCurrentLanguageValue();
    // Инициализируем тексты сразу с fallback значениями
    this.loginText = 'Войти';
    this.myRequestsText = 'Личный кабинет';
    this.requestsText = 'Запросы';
    this.personalCabinetText = 'Личный кабинет';
    this.searchOrdersText = 'Поиск запросов';
    this.usersText = 'Админ-панель';
    this.updateTranslations();
    this.checkAuthStatus();
    
    // Подписываемся на изменения языка
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(lang => {
        this.currentLanguage = lang;
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );

    // Подписываемся на изменения роутера для обновления статуса авторизации
    this.subscriptions.add(
      this.router.events.subscribe(() => {
        this.checkAuthStatus();
      })
    );

    this.currencyRates.start();
    this.subscriptions.add(
      this.currencyRates.getRates().subscribe((r) => {
        this.rates = { ...r };
        this.cdr.markForCheck();
      })
    );
  }

  checkAuthStatus(): void {
    this.isAuthenticated = this.authService.isAuthenticated();
    this.currentUser = this.authService.getUser();
    this.isAdmin = this.currentUser?.role?.code === 'ROLE_ADMIN';
    this.isCargoOwner = this.currentUser?.role?.code === 'ROLE_CARGO_OWNER';
    this.isFreightForwarder = this.currentUser?.role?.code === 'ROLE_FREIGHT_FORWARDER';
    this.isCustomsBroker = this.currentUser?.role?.code === 'ROLE_CUSTOMS_BROKER';
    this.isPublic = this.currentUser?.role?.code === 'ROLE_PUBLIC';
    
    // Отладочная информация
    console.log('Header: Auth status check', {
      isAuthenticated: this.isAuthenticated,
      currentUser: this.currentUser,
      role: this.currentUser?.role,
      roleCode: this.currentUser?.role?.code,
      isAdmin: this.isAdmin,
      isCargoOwner: this.isCargoOwner,
      isFreightForwarder: this.isFreightForwarder,
      isCustomsBroker: this.isCustomsBroker,
      isPublic: this.isPublic
    });
    
    this.cdr.detectChanges();
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  ngOnDestroy(): void {
    this.currencyRates.stop();
    this.subscriptions.unsubscribe();
  }

  switchLanguage(lang: LanguageCode): void {
    this.languageService.setLanguage(lang);
  }

  private updateTranslations(): void {
    const whatsappTranslated = this.languageService.translate('contacts.whatsappLabel');
    this.whatsappLabel = whatsappTranslated && whatsappTranslated !== 'contacts.whatsappLabel' ? whatsappTranslated : 'Написать в WhatsApp';

    const loginTranslated = this.languageService.translate('header.login');
    this.loginText = loginTranslated && loginTranslated !== 'header.login' ? loginTranslated : 'Войти';
    
    const myRequestsTranslated = this.languageService.translate('header.myRequests');
    this.myRequestsText = myRequestsTranslated && myRequestsTranslated !== 'header.myRequests' ? myRequestsTranslated : 'Личный кабинет';
    
    const requestsTranslated = this.languageService.translate('header.requests');
    this.requestsText = requestsTranslated && requestsTranslated !== 'header.requests' ? requestsTranslated : 'Запросы';
    
    const personalCabinetTranslated = this.languageService.translate('header.personalCabinet');
    this.personalCabinetText = personalCabinetTranslated && personalCabinetTranslated !== 'header.personalCabinet' ? personalCabinetTranslated : 'Личный кабинет';
    
    const searchOrdersTranslated = this.languageService.translate('header.searchOrders');
    this.searchOrdersText = searchOrdersTranslated && searchOrdersTranslated !== 'header.searchOrders' ? searchOrdersTranslated : 'Поиск запросов';
    
    const usersTranslated = this.languageService.translate('header.users');
    this.usersText = usersTranslated && usersTranslated !== 'header.users' ? usersTranslated : 'Админ-панель';
  }
}

